import * as THREE from 'three';
import { Arena } from './Arena.js';
import { Base } from './Base.js';
import { PLAYER_COLORS, BASE_POSITIONS } from '../../../utils/constants.js';

// One full day passes every few minutes of play. The sun circles the arena and
// never sets, so shadows sweep round smoothly and the field stays readable at night.
const DAY_LENGTH_SECONDS = 360;

// Lobby backdrop: late golden hour
const LOBBY_TIME = 0.32;

const KEYFRAMES = [
  { t: 0.00, name: 'afternoon', elevation: 52, sun: 0xfff1d6, sunIntensity: 3.0, hemiSky: 0xbcd7ff, hemiGround: 0x6b6a3a, hemi: 1.1, skyTop: 0x4f8fd6, skyHorizon: 0xd3e6f0, fog: 0xcfdde3, cloud: 0xffffff, exposure: 1.0, night: 0 },
  { t: 0.30, name: 'golden hour', elevation: 26, sun: 0xffd29a, sunIntensity: 3.2, hemiSky: 0xd0d4ea, hemiGround: 0x6a5a35, hemi: 1.1, skyTop: 0x5b84c4, skyHorizon: 0xf3d2a2, fog: 0xe6d0ac, cloud: 0xffe6c8, exposure: 1.05, night: 0 },
  { t: 0.45, name: 'sunset', elevation: 15, sun: 0xffa060, sunIntensity: 3.2, hemiSky: 0xb6aad4, hemiGround: 0x5a4632, hemi: 1.2, skyTop: 0x3f5a9a, skyHorizon: 0xf59a62, fog: 0xc98a72, cloud: 0xffb08a, exposure: 1.15, night: 0.35 },
  { t: 0.55, name: 'dusk', elevation: 22, sun: 0xa8bce8, sunIntensity: 2.0, hemiSky: 0x7484b8, hemiGround: 0x3a3844, hemi: 1.3, skyTop: 0x1c284d, skyHorizon: 0x6c5f8a, fog: 0x4d4c6a, cloud: 0x8a86a8, exposure: 1.3, night: 0.9 },
  { t: 0.75, name: 'night', elevation: 38, sun: 0xa8bef0, sunIntensity: 1.9, hemiSky: 0x6a7cb0, hemiGround: 0x34343f, hemi: 1.35, skyTop: 0x0f1733, skyHorizon: 0x37406a, fog: 0x2f3550, cloud: 0x5f6584, exposure: 1.35, night: 1 },
  { t: 0.88, name: 'dawn', elevation: 16, sun: 0xffb08a, sunIntensity: 2.8, hemiSky: 0xc4b2d6, hemiGround: 0x4a3f38, hemi: 1.15, skyTop: 0x5a6fae, skyHorizon: 0xf6b9a0, fog: 0xd8b3a8, cloud: 0xffd0c0, exposure: 1.1, night: 0.25 },
  { t: 1.00, name: 'afternoon', elevation: 52, sun: 0xfff1d6, sunIntensity: 3.0, hemiSky: 0xbcd7ff, hemiGround: 0x6b6a3a, hemi: 1.1, skyTop: 0x4f8fd6, skyHorizon: 0xd3e6f0, fog: 0xcfdde3, cloud: 0xffffff, exposure: 1.0, night: 0 }
];

const COLOR_KEYS = ['sun', 'hemiSky', 'hemiGround', 'skyTop', 'skyHorizon', 'fog', 'cloud'];
const NUMBER_KEYS = ['elevation', 'sunIntensity', 'hemi', 'exposure', 'night'];

const SHADOW_EXTENT = 85;
const SHADOW_MAP_SIZE = 2048;

export class Scene {
  constructor() {
    this.scene = new THREE.Scene();
    this.bases = [];
    this.timeOfDay = LOBBY_TIME;
    this.matchClock = null;
    this.day = this.createDayState();
    this.setupLighting();
    this.createArena();
    this.applyDay(this.timeOfDay);
  }

  createDayState() {
    const day = { sunDirection: new THREE.Vector3(), night: 0, exposure: 1 };
    for (const key of COLOR_KEYS) day[key] = new THREE.Color();
    return day;
  }

  setupLighting() {
    this.scene.fog = new THREE.Fog(0xcfdde3, 160, 700);

    this.hemiLight = new THREE.HemisphereLight(0xbcd7ff, 0x6b6a3a, 1.1);
    this.scene.add(this.hemiLight);

    // Sun (or moon), whose shadow box follows the action
    this.sun = new THREE.DirectionalLight(0xfff1d6, 3);
    this.sun.castShadow = true;
    const shadowCamera = this.sun.shadow.camera;
    shadowCamera.left = -SHADOW_EXTENT;
    shadowCamera.right = SHADOW_EXTENT;
    shadowCamera.top = SHADOW_EXTENT;
    shadowCamera.bottom = -SHADOW_EXTENT;
    shadowCamera.near = 1;
    shadowCamera.far = 500;
    this.sun.shadow.mapSize.set(SHADOW_MAP_SIZE, SHADOW_MAP_SIZE);
    this.sun.shadow.bias = -0.0004;
    this.sun.shadow.normalBias = 0.04;
    this.scene.add(this.sun);
    this.scene.add(this.sun.target);
  }

  createArena() {
    this.arena = new Arena(this.scene);
  }

  createBases(players) {
    // Clear existing bases
    this.bases.forEach(base => base.remove());
    this.bases = [];

    // Create bases for each player
    players.forEach((player, index) => {
      if (player) {
        const position = BASE_POSITIONS[index];
        const color = PLAYER_COLORS[index];
        const base = new Base(this.scene, position, color);
        this.bases.push(base);
      }
    });
  }

  // Start the day running from early afternoon when a match begins.
  startMatchClock() {
    this.matchClock = 0;
  }

  stopMatchClock() {
    this.matchClock = null;
    this.timeOfDay = LOBBY_TIME;
  }

  // Interpolate the keyframes into the shared day state.
  applyDay(t) {
    let i = 0;
    while (i < KEYFRAMES.length - 2 && KEYFRAMES[i + 1].t <= t) i++;
    const a = KEYFRAMES[i];
    const b = KEYFRAMES[i + 1];
    const f = THREE.MathUtils.smoothstep(t, a.t, b.t);

    const day = this.day;
    const colorA = new THREE.Color();
    const colorB = new THREE.Color();
    for (const key of COLOR_KEYS) {
      day[key].copy(colorA.set(a[key])).lerp(colorB.set(b[key]), f);
    }
    for (const key of NUMBER_KEYS) {
      day[key] = THREE.MathUtils.lerp(a[key], b[key], f);
    }

    const elevation = THREE.MathUtils.degToRad(day.elevation);
    // A full turn per day, starting from the south-west so the default view is backlit warmly
    const azimuth = t * Math.PI * 2 + THREE.MathUtils.degToRad(220);
    day.sunDirection.set(
      Math.cos(elevation) * Math.sin(azimuth),
      Math.sin(elevation),
      Math.cos(elevation) * Math.cos(azimuth)
    ).normalize();

    this.sun.color.copy(day.sun);
    this.sun.intensity = day.sunIntensity;
    this.hemiLight.color.copy(day.hemiSky);
    this.hemiLight.groundColor.copy(day.hemiGround);
    this.hemiLight.intensity = day.hemi;
    this.scene.fog.color.copy(day.fog);
  }

  // focus: the point the camera is looking at
  update(deltaTime, elapsed, focus, renderer) {
    if (this.matchClock !== null) {
      this.matchClock += deltaTime;
      this.timeOfDay = (this.matchClock / DAY_LENGTH_SECONDS) % 1;
    }
    this.applyDay(this.timeOfDay);

    if (renderer) renderer.toneMappingExposure = this.day.exposure;

    // Keep the shadow box centred on the action, snapped to whole shadow texels
    // so shadow edges don't shimmer as the camera moves
    const texel = (SHADOW_EXTENT * 2) / SHADOW_MAP_SIZE;
    const cx = Math.round(focus.x / texel) * texel;
    const cz = Math.round(focus.z / texel) * texel;
    this.sun.target.position.set(cx, 0, cz);
    this.sun.position.set(cx, 0, cz).addScaledVector(this.day.sunDirection, 250);

    this.arena.update(deltaTime, elapsed, this.day, focus);
    for (const base of this.bases) base.update?.(elapsed, this.day);
  }

  getOccluders() {
    return this.arena.getOccluders();
  }

  getScene() {
    return this.scene;
  }
}
